/**
 * SdkGameScene — base class for every game scene built on the platform.
 *
 * Subclasses implement the game-specific bits and inherit a stable contract:
 *
 *   preload()                  Phaser preload — only gameplay assets.
 *   start({ players, seed,     Called once when the match starts. Spawn the world.
 *         settings, sdk })
 *   tick(dt, inputs)           Fixed-timestep tick on the host. `inputs` is
 *                              `{ playerId: { up, down, ... } }`.
 *   tickClient(dt, myInput)    Optional. Client-side prediction tick.
 *   snapshot()                 Return a serializable view of world state
 *                              (host only).
 *   applySnapshot(snap)        Restore world state (clients only).
 *   onSdkEvent(name, payload)  Custom events emitted via `sdk.emit(...)`.
 *
 * Helpers available on `this.sdk` (set during `init`):
 *   isHost, players, settings, seed, audio, input, i18n, qualityScaler,
 *   rng, emit(name, payload), endMatch(result)
 */
export class SdkGameScene extends Phaser.Scene {
  constructor() {
    super('UserGame');
  }

  init(data) {
    this.sdk = data.sdk;
    this.players = data.players || [];
    this.seed = data.seed;
    this.settings = data.settings || {};
  }

  // Override-points — defaults are no-ops so a tiny game can pick the
  // bits it cares about and ignore the rest.
  start(_ctx) {}
  tick(_dt, _inputs) {}
  snapshot() { return null; }
  applySnapshot(_snap) {}
  onSdkEvent(_name, _payload) {}
  /** Override to reflow HUD / layout when the window is resized or
   * the device is rotated. Called with the new scene dimensions. */
  onResize(_width, _height) {}

  create() {
    this.start({
      players: this.players,
      seed: this.seed,
      settings: this.settings
    });

    // Dispatch window/orientation resizes to the user scene's
    // `onResize` hook. Debounced lightly so drag-resize on desktop
    // doesn't thrash. The scale listener is auto-removed on shutdown.
    let pendingTimer = null;
    const handler = () => {
      if (pendingTimer != null) clearTimeout(pendingTimer);
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        try { this.onResize(this.scale.width, this.scale.height); }
        catch (err) { if (typeof console !== 'undefined') console.error('[onResize]', err); }
      }, 80);
    };
    this.scale.on('resize', handler);
    this.events.once('shutdown', () => {
      if (pendingTimer != null) clearTimeout(pendingTimer);
      this.scale.off('resize', handler);
    });
  }
}
